import React from "react";
import {
  Page,
  Spinner,
  Title,
  css,
  spacing,
  typographyColors,
  uiColors,
  utilityColors,
} from "../library";
import { colors, styles } from "../library/theme";
import ChatComposer from "./ChatComposer";
import ChatMessageList from "./ChatMessageList";

// Halo suave em torno do ponto de status, no tom mint (#aadeca) da paleta.
const mintGlow = "rgba(170, 222, 202, 0.30)";

// Largura da coluna: cabeçalho, card e rodapé compartilham o mesmo eixo, para
// no desktop nada ficar "solto" nas laterais (feedback: estranho no PC).
const COLUMN = 860;

const shell = css({
  maxWidth: COLUMN,
  margin: "0 auto",
});
const pageHead = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: spacing.s,
  marginBottom: spacing.m,
  "> h2": { margin: 0, color: typographyColors.defaultTitle },
});
const chat = css(styles.cardShadow, {
  display: "flex",
  flexDirection: "column",
  height: "min(74vh, 720px)",
  minHeight: 420,
  backgroundColor: colors.white,
  borderRadius: 18,
  border: `1px solid ${uiColors.border}`,
  overflow: "hidden",
});
const errorClass = css({
  padding: `${spacing.xs} ${spacing.m}`,
  backgroundColor: colors.decorationSoft,
  color: utilityColors.danger,
  fontSize: 13,
});
const centered = css({
  display: "flex",
  justifyContent: "center",
  padding: spacing.xl,
});
const statusPill = css({
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  fontSize: 12,
  fontWeight: 500,
  color: typographyColors.defaultBody,
  backgroundColor: colors.white,
  border: `1px solid ${uiColors.border}`,
  padding: "6px 12px",
  borderRadius: 999,
});
const statusDot = css({
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: colors.decorationHard,
  boxShadow: `0 0 0 4px ${mintGlow}`,
});
const hint = css({
  margin: `${spacing.s} 0 0`,
  fontSize: 11.5,
  color: typographyColors.subtitle,
  textAlign: "center",
});

export default function AgentChat({
  agent,
  messages,
  loading,
  forbidden,
  sending,
  streamingMessageId,
  error,
  onSend,
}) {
  if (forbidden) {
    return (
      <Page title="Assistente indisponível">
        <p>Você não tem acesso a este assistente.</p>
      </Page>
    );
  }

  if (loading && !agent) {
    return (
      <Page title="Assistente">
        <div className={centered}>
          <Spinner />
        </div>
      </Page>
    );
  }

  // Sugestões de início configuradas por agente (viram a primeira mensagem ao
  // clicar). Sem nenhuma, o texto de apoio não cita "atalho".
  const starters = agent?.starters || [];

  return (
    <Page>
      <div className={shell}>
        <div className={pageHead}>
          <Title as="h2" strong>
            {agent?.page_title || agent?.name || "Assistente"}
          </Title>
          <span className={statusPill}>
            <span className={statusDot} />
            Assistente online
          </span>
        </div>
        <div className={chat}>
          <ChatMessageList
            messages={messages}
            sending={sending}
            streamingMessageId={streamingMessageId}
            suggestions={starters}
            onSuggestion={onSend}
            emptyMessage={
              starters.length > 0
                ? "Mande uma mensagem para começar ou use um dos atalhos abaixo."
                : "Mande uma mensagem para começar."
            }
          />
          {error && <div className={errorClass}>{error}</div>}
          <ChatComposer onSend={onSend} disabled={sending} />
        </div>
        <p className={hint}>
          As respostas são geradas por inteligência artificial e podem conter
          erros. Confira informações importantes.
        </p>
      </div>
    </Page>
  );
}
